import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import ActionMenu from './ActionMenu';
import CopyCollectionUrlButton from './CopyCollectionUrlButton';
import IndexDocsButton from './IndexDocsButton';
import ReloadCollectionButton from './ReloadCollectionButton';
import { formatDateTime, uptimeSecondsFormat } from '../../../utils/timeUtils';
import {
  getSolrSubdomain,
  isSolrCloud,
  sentenceCase,
} from '../../../utils/utils';
import Text from '../../elements/text/Text';
import NoContent from '../../sections/no-content/NoContent';

const InstanceCollectionSection = ({
  collections,
  instance,
  onRefresh,
  isProcessing,
}) => {
  const { t } = useTranslation();

  const [solrDomain, setSolrDomain] = useState('');

  const { subdomain, type, category } = instance;

  useEffect(() => {
    setSolrDomain(
      getSolrSubdomain(subdomain, type, category, { isCluster: true })
    );
  }, [instance]);

  return (
    <section>
      <Text sx={{ mb: 2 }}>
        {t('collectionDescription', { ns: 'descriptions' })}
      </Text>
      <Text subtitle>{t('collections', { ns: 'titles' })}</Text>
      {isProcessing && <LinearProgress />}
      {isEmpty(collections) ? (
        <NoContent
          title={sentenceCase(
            t('empty', {
              ns: 'descriptions',
              text: t('collection', { ns: 'instance' }),
            })
          )}
        />
      ) : (
        <List data-testid="collection-list">
          {collections.map((collection) => (
            <div key={collection.name}>
              <ListItem
                secondaryAction={
                  <>
                    <IndexDocsButton
                      name={collection.name}
                      category={instance.category}
                      onRefresh={onRefresh}
                    />
                    <ReloadCollectionButton
                      name={collection.name}
                      onRefresh={onRefresh}
                    />
                    <CopyCollectionUrlButton
                      name={collection.name}
                      domain={solrDomain}
                    />
                    <ActionMenu
                      collection={collection}
                      isSolrCloud={isSolrCloud(instance?.type)}
                      onRefresh={onRefresh}
                    />
                  </>
                }
              >
                <ListItemText
                  primary={collection.name}
                  secondary={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Text caption>
                          <Trans
                            i18nKey="startTime"
                            ns="instance"
                            shouldUnescape
                            values={{
                              time: formatDateTime(collection.startTime),
                            }}
                          />
                        </Text>
                        <Text caption>
                          <Trans
                            i18nKey="uptime"
                            ns="instance"
                            values={{
                              time: uptimeSecondsFormat(collection.uptime),
                            }}
                          />
                        </Text>
                      </Box>
                      {isSolrCloud(instance?.type) && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Text caption>
                            {t('shards', { ns: 'fields' })}: {collection.shards}
                          </Text>
                          <Text caption>
                            {t('replicationFactor', { ns: 'fields' })}:{' '}
                            {collection.replicationFactor}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              <Divider />
            </div>
          ))}
        </List>
      )}
    </section>
  );
};

InstanceCollectionSection.propTypes = {
  collections: PropTypes.array.isRequired,
  instance: PropTypes.object.isRequired,
  isProcessing: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired,
};

export default InstanceCollectionSection;
